import type {
  SiteHealthFactors,
  SiteOverviewSummary,
} from 'src/graphql/entities/address-summary';
import type { CitedConversation } from './reasoning-popover';
import type { Tone } from './site-overview.styles';

export const FACTOR_LABELS: { id: keyof SiteHealthFactors; label: string }[] = [
  { id: 'clientEngagement', label: 'Client engagement' },
  { id: 'resolutionEfficiency', label: 'Resolution efficiency' },
  { id: 'systemStability', label: 'System stability' },
  { id: 'supportBurden', label: 'Support burden' },
];

const CLOSED_STATUSES = ['solved', 'closed', 'resolved', 'completed'];
const MAX_CITED_REFERENCES = 5;
const PLACEHOLDER_SUBJECT = /^[-–—]*$/;

export function riskTone(risk: string | null): Tone {
  if (risk === 'low') {
    return 'good';
  }
  if (risk === 'high' || risk === 'critical') {
    return 'bad';
  }
  if (risk === 'medium') {
    return 'warn';
  }
  return 'neut';
}

export function trendTone(trend: string): Tone {
  if (trend === 'declining') {
    return 'warn';
  }
  if (trend === 'improving') {
    return 'good';
  }
  return 'neut';
}

export function sentimentTone(overall: string): Tone {
  if (overall === 'positive' || overall === 'very_positive') {
    return 'good';
  }
  if (overall === 'negative' || overall === 'very_negative') {
    return 'bad';
  }
  return 'neut';
}

export function humanize(value: string): string {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

function isClosed(status: string): boolean {
  return CLOSED_STATUSES.includes(status.trim().toLowerCase());
}

/** Collects every stored action, in priority order, without repeats. */
export function briefActions(overview: SiteOverviewSummary): string[] {
  const candidates = [
    ...(overview.siteHealth.attentionItems ?? []),
    ...overview.supportHistory.openIssues
      .filter((issue) => !isClosed(issue.status))
      .map((issue) => issue.nextAction),
    ...(overview.supportHistory.recurringIssues ?? []).map((issue) => issue.suggestedAction),
  ];

  const seen = new Set<string>();
  return candidates.filter((action): action is string => {
    const text = action?.trim();
    if (!text || seen.has(text)) {
      return false;
    }
    seen.add(text);
    return true;
  });
}

export function citedConversationIds(overview: SiteOverviewSummary): string[] {
  const { siteHealth, clientSentiment, supportHistory } = overview;
  const drivers = [
    ...(clientSentiment.sentimentDrivers.positive ?? []),
    ...(clientSentiment.sentimentDrivers.negative ?? []),
  ];

  return [
    ...new Set(
      [
        ...siteHealth.conversationIds,
        ...clientSentiment.conversationIds,
        ...drivers.flatMap((driver) => driver.conversationIds),
        ...supportHistory.openIssues.map((issue) => issue.friendlyId),
        ...supportHistory.recentResolutions.map((resolution) => resolution.friendlyId),
      ].filter((id) => id.trim().length > 0),
    ),
  ];
}

function isPlaceholderSubject(subject: string): boolean {
  return PLACEHOLDER_SUBJECT.test(subject.trim());
}

export function resolveReferences(
  overview: SiteOverviewSummary,
  friendlyIds: string[] | null,
  resolved: Map<string, string>,
): CitedConversation[] {
  if (!friendlyIds?.length) {
    return [];
  }

  const fromBlob = new Map<string, string>();
  for (const issue of overview.supportHistory.openIssues) {
    fromBlob.set(issue.friendlyId, issue.subject);
  }
  for (const resolution of overview.supportHistory.recentResolutions ?? []) {
    fromBlob.set(resolution.friendlyId, resolution.issue);
  }

  const picked: CitedConversation[] = [];
  for (const friendlyId of new Set(friendlyIds)) {
    const subject = resolved.get(friendlyId) ?? fromBlob.get(friendlyId) ?? '';
    if (isPlaceholderSubject(subject)) {
      continue;
    }
    picked.push({ friendlyId, subject });
    if (picked.length === MAX_CITED_REFERENCES) {
      break;
    }
  }
  return picked;
}
